import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth, formatApiError } from "@/auth";
import { User, ShieldCheck, HardDrive, Download, Save, Lock, Loader2, Terminal } from "lucide-react";

const DRIVERS = [
  { id: "mtk", label: "MediaTek USB VCOM Drivers", size: "17.4 MB" },
  { id: "qc", label: "Qualcomm HS-USB QDLoader 9008", size: "9.1 MB" },
  { id: "spd", label: "Spreadtrum/Unisoc SPD USB", size: "6.2 MB" },
  { id: "samsung", label: "Samsung Smart Switch (Android)", size: "62.0 MB" },
  { id: "apple", label: "Apple Mobile Device Support", size: "31.5 MB" },
];

export default function ParametresPage() {
  const { user, refreshMe } = useAuth();
  const [profile, setProfile] = useState({ name: "", workshop: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pmsg, setPmsg] = useState(null);
  const [wmsg, setWmsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", workshop: user.workshop || "" });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true); setPmsg(null);
    try {
      await axios.patch(`${API}/auth/profile`, profile);
      await refreshMe();
      setPmsg({ ok: true, text: "Profil mis à jour." });
    } catch (err) { setPmsg({ ok: false, text: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault(); setWmsg(null);
    if (pw.new_password !== pw.confirm) {
      setWmsg({ ok: false, text: "Les mots de passe ne correspondent pas" });
      return;
    }
    if (pw.new_password.length < 6) {
      setWmsg({ ok: false, text: "Minimum 6 caractères" });
      return;
    }
    setChanging(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      setWmsg({ ok: true, text: "Mot de passe changé avec succès." });
    } catch (err) { setWmsg({ ok: false, text: formatApiError(err) }); }
    finally { setChanging(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      <div className="card-lhs p-6">
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-tech">Compte</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Paramètres</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile */}
        <div className="card-lhs p-5">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-orange-500" />
            <h2 className="font-display font-bold uppercase tracking-wider text-sm">Profil technicien</h2>
          </div>
          <form onSubmit={saveProfile} className="space-y-3">
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1 font-mono-tech">Nom complet</label>
              <input data-testid="profile-name" className="input-lhs" value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1 font-mono-tech">Atelier / Boutique</label>
              <input data-testid="profile-workshop" className="input-lhs" value={profile.workshop}
                onChange={(e) => setProfile({ ...profile, workshop: e.target.value })} />
            </div>
            <div className="text-xs font-mono-tech text-zinc-500 space-y-1">
              <div>Email: <span className="text-zinc-300">{user?.email}</span> (non modifiable)</div>
              <div>Rôle: <span className="text-orange-400 uppercase">{user?.role}</span></div>
            </div>
            {pmsg && (
              <div className={`text-sm px-3 py-2 rounded-lg border ${pmsg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-red-400 border-red-500/40 bg-red-500/10"}`}>
                {pmsg.text}
              </div>
            )}
            <button data-testid="profile-save" disabled={saving} className="btn-orange rounded-lg px-4 py-2 text-sm flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
            </button>
          </form>
        </div>

        {/* Password */}
        <div className="card-lhs p-5">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-orange-500" />
            <h2 className="font-display font-bold uppercase tracking-wider text-sm">Changer le mot de passe</h2>
          </div>
          <form onSubmit={changePassword} className="space-y-3">
            <input data-testid="pw-current" type="password" required placeholder="Mot de passe actuel" className="input-lhs"
              value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
            <input data-testid="pw-new" type="password" required minLength={6} placeholder="Nouveau mot de passe (6+ car.)" className="input-lhs"
              value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
            <input data-testid="pw-confirm" type="password" required placeholder="Confirmer le nouveau mot de passe" className="input-lhs"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
            {wmsg && (
              <div className={`text-sm px-3 py-2 rounded-lg border ${wmsg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-red-400 border-red-500/40 bg-red-500/10"}`}>
                {wmsg.text}
              </div>
            )}
            <button data-testid="pw-submit" disabled={changing} className="btn-orange rounded-lg px-4 py-2 text-sm flex items-center gap-2">
              {changing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Changer
            </button>
          </form>
        </div>

        {/* Security snapshot */}
        <div className="card-lhs p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-orange-500" />
            <h2 className="font-display font-bold uppercase tracking-wider text-sm">Sécurité & Licence</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono-tech">
            <Info k="Licence" v={user?.license_status?.toUpperCase()} accent={user?.license_status === "active"} />
            <Info k="Formule" v={user?.license_plan || "—"} />
            <Info k="Serveur" v="lhsunlocker.com" />
            <Info k="Chiffrement" v="TLS 1.3 · JWT" />
          </div>
          <div className="mt-3 text-[11px] text-zinc-500">
            Anti-crack: vérification serveur, contrôle d'intégrité, jetons signés HS256, sessions liées au compte.
          </div>
        </div>

        {/* Desktop bridge */}
        <div className="card-lhs p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Terminal className="w-4 h-4 text-orange-500" />
            <h2 className="font-display font-bold uppercase tracking-wider text-sm">Client Windows LHSunlocker</h2>
          </div>
          <p className="text-sm text-zinc-400">
            Un client Electron Windows peut se connecter au serveur pour piloter les vrais ports USB et streamer les logs vers ce dashboard.
            Le code source est prêt dans <code className="text-orange-400 font-mono-tech">/app/desktop-client/</code>.
          </p>
          <div className="mt-3 rounded-lg border border-zinc-800 bg-[#0C0C10] p-3 text-xs font-mono-tech text-zinc-300 space-y-1">
            <div><span className="text-zinc-500"># 1) Récupérer votre jeton depuis Paramètres → console navigateur:</span></div>
            <div>localStorage.getItem('lhs_token')</div>
            <div className="text-zinc-500 mt-2"># 2) Sur Windows:</div>
            <div>cd desktop-client &amp;&amp; npm install &amp;&amp; npm run dev</div>
            <div className="text-zinc-500 mt-2"># 3) Créer l'installateur .exe:</div>
            <div>npm run build</div>
          </div>
        </div>

        {/* Drivers */}
        <div className="card-lhs p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <HardDrive className="w-4 h-4 text-orange-500" />
            <h2 className="font-display font-bold uppercase tracking-wider text-sm">Drivers USB Windows</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {DRIVERS.map(d => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-[#0C0C10] p-3">
                <div>
                  <div className="text-sm font-semibold">{d.label}</div>
                  <div className="text-[11px] text-zinc-500 font-mono-tech">{d.size} · Windows 7/8/10/11</div>
                </div>
                <button data-testid={`driver-${d.id}`} className="btn-orange px-3 py-2 rounded-lg text-xs flex items-center gap-2">
                  <Download className="w-3.5 h-3.5" /> Télécharger
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ k, v, accent }) {
  return (
    <div className="card-lhs !rounded-lg p-3">
      <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{k}</div>
      <div className={`text-sm font-semibold mt-1 ${accent ? "text-emerald-400" : "text-white"}`}>{v}</div>
    </div>
  );
}
